import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';

import { Inject, Injectable } from '@angular/core';
import { AngularFireMessaging } from '@angular/fire/compat/messaging';
import { Router } from '@angular/router';

import { Observable } from 'rxjs';
import { filter, map, switchMap, take, withLatestFrom } from 'rxjs/operators';

import { MwAuthService } from '../../auth/services/auth.service';
import { Config, MW_APP_CONFIG } from '../../config';
import { userActions, userReducers } from '../@store';
import {
  ROLE_SUPER_ADMIN,
  ROLE_SYSTEM_ADMIN,
  UserProfileModel,
} from '../models';
import { CompanyViewModel } from './../models/company-view.model';
import { LocalStorageService } from './local-storage.service';
import { MwUserApiService } from './user-api.service';

@Injectable()
export class MwUserService {
  static nextId = 0;
  id: string = `${this.config.environment.clientId}-${MwUserService.nextId++}`;

  constructor(
    private readonly store: Store<any>,
    private readonly router: Router,
    private readonly actions: Actions,
    private readonly userApiService: MwUserApiService,
    private readonly afMessaging: AngularFireMessaging,
    private readonly authService: MwAuthService,
    @Inject(MW_APP_CONFIG) private config: Config
  ) {}

  get userLanguage$(): Observable<string | null> {
    return this.store
      .select(userReducers.getUserLanguage)
      .pipe(filter((language) => !!language));
  }

  get hasAccess$(): Observable<boolean | null> {
    return this.store.select(userReducers.hasAccess);
  }

  get userProfile$(): Observable<UserProfileModel | null> {
    return this.authService.loggedIn$.pipe(
      filter((isLoggedIn) => isLoggedIn),
      switchMap(() => this.authService.userData$),
      filter((userData) => !!userData),
      switchMap(() => this.store.select(userReducers.getUserProfile))
    );
  }

  get isSuperAdmin$(): Observable<boolean> {
    return this.userProfile$.pipe(
      map(
        (userProfile) =>
          userProfile?.roles.findIndex((t) => t === ROLE_SUPER_ADMIN) !== -1
      )
    );
  }

  get messages$(): Observable<any> {
    return this.afMessaging.messages.pipe(
      withLatestFrom(this.userProfile$),
      filter(([message, userProfile]) => {
        const data = (message as any)?.data;
        const companyNotMatch =
          data &&
          data.CompanyId &&
          Number(data.CompanyId) !== userProfile?.companyId;
        const userNotMatch =
          data && data.UserId && Number(data.UserId) !== userProfile?.userId;

        if (companyNotMatch || userNotMatch) {
          return false;
        }

        return true;
      }),
      map(([message]) => message)
    );
  }

  get isSystemAdmin$(): Observable<boolean> {
    return this.userProfile$.pipe(
      map(
        (userProfile) =>
          userProfile?.roles.findIndex(
            (t) => t === ROLE_SUPER_ADMIN || t === ROLE_SYSTEM_ADMIN
          ) !== -1
      )
    );
  }

  changeLanguage(language: string): void {
    this.store.dispatch(userActions.changeLanguage({ userLanguage: language }));
  }

  changeCompany(opts?: {
    selectedCompanyId?: number;
    isSaveCurrentRoute?: boolean;
  }): void {
    this.store.dispatch(
      userActions.changeCompany({
        selectedCompanyId: opts?.selectedCompanyId,
        isSaveCurrentRoute: opts?.isSaveCurrentRoute,
      })
    );
  }

  refreshUserProfile(): void {
    this.userProfile$.pipe(take(1)).subscribe((profile) => {
      this.store.dispatch(
        userActions.requestUserProfile({ companyId: profile?.companyId })
      );
    });
  }

  /**
   * Returns list of companies
   * @param includeCompanyId - include company by id
   * @returns Observable<CompanyViewModel[]>
   */
  getCompanies(includeCompanyId?: number): Observable<CompanyViewModel[]> {
    return this.userApiService.getCompanies(includeCompanyId);
  }

  /**
   * Set active company and trigger profiler request.
   *  - If user has access to module he is redirected to '/' route
   *  - If user has no access he is redirected to 'no-access' route
   * @param companyId - Company Id
   * @param language - Default language
   */
  setActiveCompanyById(companyId: number, language?: string): void {
    this.userApiService
      .getCompany(companyId)
      .pipe(take(1))
      .subscribe((company) => {
        this.store.dispatch(
          userActions.setActiveCompany({
            company,
            hasManyCompanies: false,
            language,
          })
        );

        this.actions
          .pipe(ofType(userActions.setUserProfile), take(1))
          .subscribe((profile) => {
            const hasAccess =
              profile.userApiProfile?.hasAccessToModule === true;
            const storedRoute = LocalStorageService.getStoredRedirectRoute();

            // Saved route is used in setUserProfile effect (oidc.effects.ts  )
            // so here we need to skip navigation.
            if (!storedRoute || !hasAccess) {
              this.router.navigate([
                hasAccess && !storedRoute ? '/' : 'no-access',
              ]);
            }
          });
      });
  }
}
